/**
 * Notion's colour palette, transcribed exactly.
 *
 * Source: https://matthiasfrank.de/en/notion-colors/
 *
 * Every hex below is Notion's own value, unmodified. This file is a faithful
 * record, not a design — it is deliberately separate from `tokens.ts` so that
 * the raw reference and our interpretation of it never get muddled. If Notion
 * publishes a change, this is the only file that needs editing.
 *
 * Notion splits each colour family three ways, and the distinction matters:
 *
 *   - `text` — the colour of coloured TEXT. Darkened for contrast on white.
 *   - `background` — a very pale tint, for highlights and callout blocks.
 *   - `icon` — the most saturated of the three, for icons and solid fills.
 *
 * Using the background value for text (or the text value for a fill) is the
 * usual way these palettes get misapplied; the names say which is which.
 */

export const NOTION_LIGHT = {
  text: {
    default: '#373530',
    gray: '#787774',
    brown: '#976D57',
    orange: '#CC782F',
    yellow: '#C29343',
    green: '#548164',
    blue: '#487CA5',
    purple: '#8A67AB',
    pink: '#B35488',
    red: '#C4554D',
  },
  background: {
    default: '#FFFFFF',
    gray: '#F1F1EF',
    brown: '#F3EEEE',
    orange: '#F8ECDF',
    yellow: '#FAF3DD',
    green: '#EEF3ED',
    blue: '#E9F3F7',
    purple: '#F6F3F8',
    pink: '#F9F2F5',
    red: '#FAECEC',
  },
  icon: {
    default: '#55534E',
    gray: '#A6A299',
    brown: '#9F6B53',
    orange: '#D87620',
    yellow: '#CB912F',
    green: '#448361',
    blue: '#337EA9',
    purple: '#9065B0',
    pink: '#C14C8A',
    red: '#D44C47',
  },
} as const;

export const NOTION_DARK = {
  text: {
    default: '#D4D4D4',
    gray: '#9B9B9B',
    brown: '#A27763',
    orange: '#CB7B37',
    yellow: '#C19138',
    green: '#4F9768',
    blue: '#447ACB',
    purple: '#865DBB',
    pink: '#BA4A78',
    red: '#BE524B',
  },
  background: {
    default: '#191919',
    gray: '#252525',
    brown: '#2E2724',
    orange: '#36291F',
    yellow: '#372E20',
    green: '#242B26',
    blue: '#1F282D',
    purple: '#2A2430',
    pink: '#2E2328',
    red: '#332523',
  },
  icon: {
    default: '#D3D3D3',
    gray: '#7F7F7F',
    brown: '#AA755F',
    orange: '#D9730D',
    yellow: '#CA8E1B',
    green: '#2D9964',
    blue: '#2E7CD1',
    purple: '#8D5BC1',
    pink: '#C94079',
    red: '#CD4945',
  },
} as const;

/** The ten families, in Notion's own order. */
export const NOTION_COLOR_NAMES = [
  'default',
  'gray',
  'brown',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
  'red',
] as const;

export type NotionColorName = (typeof NOTION_COLOR_NAMES)[number];

/** One family's three values, for one mode. */
export interface NotionColor {
  text: string;
  background: string;
  icon: string;
}

export function notionColor(name: NotionColorName, mode: 'light' | 'dark'): NotionColor {
  const palette = mode === 'dark' ? NOTION_DARK : NOTION_LIGHT;
  return {
    text: palette.text[name],
    background: palette.background[name],
    icon: palette.icon[name],
  };
}

/**
 * The families available for user-chosen labels — departments, leave types,
 * announcement categories.
 *
 * `default` is excluded: it is the absence of a colour, not a choice. Assigning
 * it to a department would make that department look unlabelled.
 */
export const NOTION_LABEL_COLORS: readonly NotionColorName[] = [
  'gray',
  'brown',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
  'red',
];

/**
 * Picks a stable colour for an arbitrary string.
 *
 * Deterministic, so a department is the same colour on every screen, for every
 * user, forever — without anyone having to store a colour against it. A random
 * or index-based choice would reshuffle the moment the list is reordered.
 */
export function colorForKey(key: string): NotionColorName {
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) | 0;
  }
  const position = Math.abs(hash) % NOTION_LABEL_COLORS.length;
  return NOTION_LABEL_COLORS[position] as NotionColorName;
}
